import React, { Dispatch, JSX, useEffect, useReducer } from 'react';
import { createContext, useContext, useContextSelector } from 'use-context-selector';
import { PayloadAction } from 'typesafe-actions';
import { auth, db } from '../../config/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { getNewDateTime } from '../../utils/utils';
import type { Roles } from '../../data/users';
import Loader from './Loader';

interface CRUD {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

interface defaultStateInterface {
  loading: boolean;
  user: string;
  role: Roles;
  permissions: {
    customers: CRUD;
    dashboard: CRUD;
    orders: CRUD;
    products: CRUD;
    users: CRUD;
    suppliers: CRUD;
    transactions: CRUD;
    settings: CRUD;
  };
}

export const defaultState: defaultStateInterface = {
  loading: true,
  user: '',
  role: 'guest',
  permissions: {
    settings: {
      read: false,
      create: false,
      update: false,
      delete: false,
    },
    dashboard: {
      read: false,
      create: false,
      update: false,
      delete: false,
    },
    customers: {
      read: true,
      create: false,
      update: false,
      delete: false,
    },
    orders: {
      read: true,
      create: false,
      update: false,
      delete: false,
    },
    products: {
      read: true,
      create: false,
      update: false,
      delete: false,
    },
    users: {
      read: false,
      create: false,
      update: false,
      delete: false,
    },
    suppliers: {
      read: true,
      create: false,
      update: false,
      delete: false,
    },
    transactions: {
      read: false,
      create: false,
      update: false,
      delete: false,
    },
  },
};

export interface AclProviderProps {
  children: JSX.Element;
}

export enum AclActions {
  Loading = '@@STATE/LOADING',
  User = '@@STATE/USER',
  Role = '@@STATE/ROLE',
  Permissions = '@@STATE/PERMISSIONS',
}

export const ActionsReducer = (state: defaultStateInterface, action: PayloadAction<AclActions, any>) => {
  // console.log(action,state);
  switch (action.type) {
    case AclActions.Loading:
      return {
        ...state,
        loading: action.payload,
      };
    case AclActions.User:
      return {
        ...state,
        user: action.payload,
      };
    case AclActions.Role:
      return {
        ...state,
        role: action.payload,
      };
    case AclActions.Permissions:
      return {
        ...state,
        permissions: {
          ...state.permissions,
          ...action.payload,
        },
      };
    default: {
      return defaultState;
    }
  }
};

const Context = createContext<defaultStateInterface>(defaultState);
const DispatchContext = createContext<Dispatch<PayloadAction<AclActions, any>> | undefined>(undefined);

export const usePermissions = () => {
  return useContextSelector(Context, (state) => state.permissions);
};

export const useUser = () => {
  return useContextSelector(Context, (state) => state.user);
};

export const useRole = () => {
  return useContextSelector(Context, (state) => state.role);
};

export const useAclDispatchProvider = (): Dispatch<PayloadAction<AclActions, any>> | undefined => {
  return useContext(DispatchContext);
};

const AclProvider: React.FC<AclProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(ActionsReducer, defaultState);

  useEffect(() => {
    const fetchPermissions = async (role: string) => {
      const aclRef = doc(db, 'config', 'acl', 'roles', role);
      try {
        const docSnap = await getDoc(aclRef);
        if (docSnap.exists()) {
          const permissionsData = docSnap.data();
          dispatch({ type: AclActions.Permissions, payload: permissionsData });
        }
      } catch (err) {}
    };
    const fetchUser = async (user: User) => {
      const userRef = doc(db, 'users', user.uid);
      try {
        const docSnap = await getDoc(userRef);
        if (docSnap.exists()) {
          const customerData = docSnap.data();
          dispatch({ type: AclActions.Role, payload: customerData.role });
          updateUser(user).then(() => {
            fetchPermissions(customerData.role).then();
          });
        } else {
          await setDoc(userRef, {
            dateSignIn: getNewDateTime(),
            email: user.email,
            photoURL: user.photoURL,
            displayName: user.displayName,
            role: 'guest',
          });
          fetchPermissions('guest').then(() => {});
        }
      } catch (err) {}
    };
    const updateUser = async (user: User) => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const newSupplierDocRef = await updateDoc(userRef, {
          dateSignIn: getNewDateTime(),
          email: user.email,
          photoURL: user.photoURL,
          displayName: user.displayName,
        });
      } catch (error) {}
    };
    auth.onAuthStateChanged((user) => {
      // console.log('onAuthStateChanged', user);
      if (user) {
        dispatch({ type: AclActions.User, payload: user?.uid });
        fetchUser(user).then(() => {
          dispatch({ type: AclActions.Loading, payload: false });
        });
      } else {
        dispatch({ type: AclActions.Loading, payload: false });
      }
    });
  }, []);

  return (
    <Context.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{state.loading ? <Loader /> : children}</DispatchContext.Provider>
    </Context.Provider>
  );
};

export default AclProvider;
